import {
  useCreatePackaging as useFarmCreatePackaging,
  useDeletePackaging as useFarmDeletePackaging,
  useUpdatePackaging as useFarmUpdatePackaging
} from "../../../../../api/farmGate/packaging";
import {
  useCreatePackaging as useProcessCreatePackaging,
  useDeletePackaging as useProcessDeletePackaging,
  useUpdatePackaging as useProcessUpdatePackaging
} from "../../../../../api/processGate/packaging";
import { errorView, successMessage } from "../../../../../helpers/ErrorHandler";
import { ApiPackagingLevelType, GateKind, PackagingMaterialRecord } from "../types/packagingDetail.types";
import { mapRecordToApiMaterialPayload } from "../utils/packagingDetail.utils";

interface UsePackagingMaterialCrudParams {
  packagingId: string;
  gateId: string;
  gateKind: GateKind;
  onSaved: () => void;
}

/**
 * Material CRUD against the dedicated per-level packaging endpoints.
 *
 * Note: on update/delete the API's own `packagingId` param actually means the
 * material's own `_id`, not the parent packaging id — the parent id travels as
 * a query param (`packagingId` on delete, `[gateIdKey]` on both). This is the
 * real (if confusingly named) API contract — don't "fix" the naming.
 */
export const usePackagingMaterialCrud = ({
  packagingId,
  gateId,
  gateKind,
  onSaved
}: UsePackagingMaterialCrudParams) => {
  const gateIdKey = gateKind === "farm" ? "farmGateId" : "processGateId";

  const { mutate: farmCreate } = useFarmCreatePackaging();
  const { mutate: farmUpdate } = useFarmUpdatePackaging();
  const { mutate: farmDelete } = useFarmDeletePackaging();
  const { mutate: processCreate } = useProcessCreatePackaging();
  const { mutate: processUpdate } = useProcessUpdatePackaging();
  const { mutate: processDelete } = useProcessDeletePackaging();

  const addMaterial = (
    level: ApiPackagingLevelType,
    values: Pick<PackagingMaterialRecord, "name" | "material" | "weight" | "weightUnit" | "cost">
  ) => {
    const payload = {
      data: mapRecordToApiMaterialPayload(values),
      packagingType: level,
      packagingId,
      [gateIdKey]: gateId
    };
    const opts = {
      onSuccess: () => {
        successMessage("Material added");
        onSaved();
      },
      onError: () => errorView("Failed to add material")
    };
    if (gateKind === "farm") {
      farmCreate(payload, opts);
    } else {
      processCreate(payload, opts);
    }
  };

  const updateMaterial = (
    level: ApiPackagingLevelType,
    materialId: string,
    patch: Partial<PackagingMaterialRecord>,
    current: PackagingMaterialRecord
  ) => {
    const merged = { ...current, ...patch };
    const payload = {
      data: mapRecordToApiMaterialPayload(merged),
      packagingType: level,
      packagingId: materialId,
      [gateIdKey]: gateId
    };
    const opts = {
      onSuccess: () => {
        successMessage("Material updated");
        onSaved();
      },
      onError: () => errorView("Failed to update material")
    };
    if (gateKind === "farm") {
      farmUpdate(payload, opts);
    } else {
      processUpdate(payload, opts);
    }
  };

  const removeMaterial = (level: ApiPackagingLevelType, materialId: string) => {
    const payload = {
      packagingType: level,
      packagingId,
      subPackagingId: materialId,
      [gateIdKey]: gateId
    };
    const opts = {
      onSuccess: () => {
        successMessage("Material removed");
        onSaved();
      },
      onError: () => errorView("Failed to remove material")
    };
    if (gateKind === "farm") {
      farmDelete(payload, opts);
    } else {
      processDelete(payload, opts);
    }
  };

  return { addMaterial, updateMaterial, removeMaterial };
};
